/* ============================================================
   galerij-embla.js — de productgalerij op mobiel (Omnify, 23-09-2026)

   WAAROM DIT BESTAND ER IS. De galerij draaide op `juno-slider`, de
   zelfgebouwde vervanger van Embla. Vijf dingen waren aanwijsbaar mis:

     1. Het beeld volgde je vinger niet. `pointermove` onthield alleen het
        aantal pixels; er werd niets verplaatst. Pas bij loslaten sprong hij.
     2. De overgang duurde 500 ms.
     3. Geen momentum: een zachte veeg deed hetzelfde als een harde flick.
     4. `pointerleave` telde als loslaten, dus verticaal scrollen met je vinger
        op de foto wisselde het beeld.
     5. Geen `touch-action`, dus vegen en scrollen vochten om dezelfde beweging.

   Embla lost alle vijf op en is wat het oorspronkelijke thema ook gebruikte.
   Het bestand staat in het thema zelf (assets/embla-carousel.js, de officiële
   UMD-build), niet bij een externe CDN.

   ALLEEN ONDER 768 px. Daarboven toont de galerij één groot beeld met een kolom
   voorbeelden ernaast, en dat is `product-galerij.js`.

   `is-active` OP DE VIEWPORT. De CSS van het origineel stuurt met
   `.is-active .slider__item` op de slides. `juno-slider` zette die klasse; hij
   laat de galerij nu met rust (`data-extern`), dus dit script zet hem.

   HERMETEN NA HET LADEN. Embla meet de slides bij het opstarten. Staat een foto
   er dan nog niet, dan meet hij een lege doos, concludeert dat alles past en
   klemt de verschuiving op nul — de stippen sprongen wél, het beeld niet. Dat
   is precies wat er hier gebeurde. Vandaar `reInit()` zodra de beelden binnen
   zijn en zodra de galerij van maat verandert.
   ============================================================ */
(() => {
  const BREEKPUNT = '(max-width: 767px)';

  class Galerij {
    constructor(wortel) {
      this.wortel = wortel;
      this.viewport = wortel.querySelector('[data-slider="viewport"], .slider__viewport');
      this.track = wortel.querySelector('.slider__track');
      this.slides = this.track ? [...this.track.children] : [];
      this.embla = null;
      this.stippen = null;
      if (!this.viewport || !this.track || this.slides.length < 2) return;

      this.mq = window.matchMedia(BREEKPUNT);
      this.mq.addEventListener('change', () => this.herzie());

      /* De galerij kan van maat veranderen zonder dat het breekpunt wisselt:
         een adresbalk die wegschuift, een telefoon die draait. Embla moet dan
         opnieuw meten, anders klopt de snap niet meer. */
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.embla && this.embla.reInit());
        this.ro.observe(this.viewport);
      }

      this.herzie();
    }

    herzie() {
      if (this.mq.matches) this.aan(); else this.uit();
    }

    aan() {
      if (this.embla || typeof window.EmblaCarousel !== 'function') return;

      this.viewport.classList.add('is-active');
      this.track.style.transform = '';
      this.track.style.transition = '';

      this.embla = window.EmblaCarousel(this.viewport, {
        align: 'start',
        containScroll: 'trimSnaps',
        loop: false,
        /* Embla's `duration` is geen milliseconden maar een stijfheid: lager is
           sneller. 20 ligt rond de 250 ms — de 500 ms hiervoor was het halve
           probleem. */
        duration: 20
      });

      // Voor de browserconsole en voor toekomstige metingen.
      this.viewport.emblaApi = this.embla;

      this.stippenBouw();
      this.embla.on('select', () => this.stippenBij());
      this.embla.on('reInit', () => this.stippenBij());
      this.stippenBij();
      this.hermeet();
    }

    /* Elke foto die nog niet binnen is, is een slide die Embla als leeg heeft
       gemeten. Zodra hij er is: opnieuw meten. `window.load` als vangnet voor
       alles wat via CSS of een placeholder binnenkomt. */
    hermeet() {
      const beelden = [...this.track.querySelectorAll('img')];
      beelden.forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => this.embla && this.embla.reInit(), { once: true });
        img.addEventListener('error', () => this.embla && this.embla.reInit(), { once: true });
      });
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.embla && this.embla.reInit(), { once: true });
      }
      requestAnimationFrame(() => this.embla && this.embla.reInit());
    }

    uit() {
      if (this.embla) { this.embla.destroy(); this.embla = null; this.viewport.emblaApi = null; }
      if (this.stippen) { this.stippen.remove(); this.stippen = null; }
      this.viewport.classList.remove('is-active');
      this.track.style.transform = '';
    }

    stippenBouw() {
      if (this.stippen) return;
      const aantal = this.embla.scrollSnapList().length;
      const lijst = document.createElement('div');
      lijst.className = 'slider__dots';
      lijst.setAttribute('role', 'tablist');
      lijst.setAttribute('aria-label', 'Beelden');
      for (let i = 0; i < aantal; i++) {
        const knop = document.createElement('button');
        knop.type = 'button';
        knop.className = 'slider__dot';
        knop.setAttribute('role', 'tab');
        knop.setAttribute('aria-label', `Beeld ${i + 1} van ${aantal}`);
        knop.addEventListener('click', () => this.embla.scrollTo(i));
        lijst.appendChild(knop);
      }
      this.wortel.appendChild(lijst);
      this.stippen = lijst;
    }

    stippenBij() {
      if (!this.stippen || !this.embla) return;
      const nu = this.embla.selectedScrollSnap();
      [...this.stippen.children].forEach((knop, i) => {
        const actief = i === nu;
        knop.classList.toggle('is-actief', actief);
        knop.setAttribute('aria-selected', actief ? 'true' : 'false');
      });
    }
  }

  const start = () => {
    document.querySelectorAll('.product-galerij .slider--media-gallery').forEach((el) => {
      if (el.dataset.emblaKlaar) return;
      el.dataset.emblaKlaar = '1';
      new Galerij(el);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
